import React, { useEffect, useState, useMemo, useCallback } from "react";
import NetworkCanvas from "./NetworkCanvas";
import DeviceDrawer from "./DeviceDrawer";
import { SkeletonStatRow } from "./Skeleton";
import { fmtBytes, fmtNum, ago } from "../utils/formatters";
import { WanLabel } from "./WanNames";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import {
  FiMonitor,
  FiActivity,
  FiHardDrive,
  FiCpu,
  FiWifi,
  FiDatabase,
  FiLayers,
  FiEyeOff,
  FiEye,
  FiToggleLeft,
  FiToggleRight,
  FiClock,
} from "react-icons/fi";

// ── Summary stat card ────────────────────────────────────────────────────────

// Classification mode metadata. "both" = application-specific rules win and
// fall back to category (then best-effort) in the data path.
const MODE_OPTIONS = [
  { id: "cat", label: "Category" },
  { id: "both", label: "Combined" },
  { id: "app", label: "Application" },
];
const MODE_LABEL = {
  app: "Per-Application",
  cat: "Per-Category",
  both: "App → Category",
};
const MODE_BADGE_CLASS = {
  app: "mode-app",
  cat: "mode-cat",
  both: "mode-both",
};
const MODE_HINT = {
  app: "Traffic is classified by specific application (Zoom, Netflix, etc.)",
  cat: "Traffic is classified by broad category (Realtime, Streaming, etc.)",
  both: "Application-specific rules take priority; flows with no matching app fall back to their category, then best-effort.",
};

function SummaryCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="dash-stat" style={{ "--accent": accent || "#6366f1" }}>
      <div className="dash-stat-icon">
        <Icon size={20} />
      </div>
      <div className="dash-stat-body">
        <div className="dash-stat-label">{label}</div>
        <div className="dash-stat-value">{value}</div>
        {sub && <div className="dash-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── System detail mini-card ──────────────────────────────────────────────────

function MiniCard({ label, value, pill }) {
  return (
    <div className="dash-mini-card">
      <div className="dash-mini-label">{label}</div>
      <div className={`dash-mini-value ${pill || ""}`}>{value}</div>
    </div>
  );
}

// ── WAN link compact card ────────────────────────────────────────────────────

function WANCompact({ wan }) {
  const isUp = wan.active !== false;
  return (
    <div className="dash-wan-compact">
      <div className="dash-wan-name">
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: isUp ? "#22c55e" : "#ef4444",
            marginRight: 6,
            verticalAlign: "middle",
          }}
        />
        <WanLabel iface={wan.name} />
      </div>
      <div className="dash-wan-stats">
        <span>↑ {fmtBytes(wan.tx_bytes)}</span>
        <span>↓ {fmtBytes(wan.rx_bytes)}</span>
        <span
          className={`dash-wan-errors ${wan.errors > 0 ? "has-errors" : ""}`}
        >
          {wan.errors > 0 ? `${fmtNum(wan.errors)} err` : "0 err"}
        </span>
      </div>
      <div className="dash-wan-meta">
        Up: {ago(wan.last_up)} · Down: {ago(wan.last_down)}
      </div>
    </div>
  );
}

// ── Main DashboardView ───────────────────────────────────────────────────────

// A phone answers /diagnostics with its links and its tunnel, and has no
// devices, host or classifier behind it, so it gets the cards that describe it
// instead of three that would read as nothing running.
export default function DashboardView({ nodeApiUrl, selectedNode, phone = false }) {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetch, setLastFetch] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  // A phone's details are its links and its tunnel, which are the point of
  // its dashboard, so they start open.
  const [systemExpanded, setSystemExpanded] = useState(phone);
  const [hideStale, setHideStale] = useState(false);
  const [classifierMode, setClassifierMode] = useState(null); // "app" | "cat" | null
  const [modeSwitching, setModeSwitching] = useState(false);

  // Poll diagnostics
  useEffect(() => {
    if (!selectedNode) {
      setDiag(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setDiag(null);
    setLoading(true);

    const doFetch = async () => {
      const url = nodeApiUrl("/diagnostics");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = await res.json();
        if (!controller.signal.aborted) {
          setDiag(data);
          setError("");
          setLastFetch(new Date());
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        if (!controller.signal.aborted)
          setError(err.message || "Failed to fetch diagnostics");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    doFetch();
    const timer = setInterval(doFetch, 5000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [selectedNode, nodeApiUrl]);

  // Keep the selected device in sync with fresh diagnostics data
  useEffect(() => {
    if (!selectedDevice || !diag?.devices) return;
    // By MAC when the device has one, else by address: comparing two missing
    // MACs matched any other device without one, and the drawer jumped to it.
    const fresh = diag.devices.find((d) =>
      selectedDevice.mac ? d.mac === selectedDevice.mac : d.ip === selectedDevice.ip
    );
    if (fresh) {
      setSelectedDevice(fresh);
    }
  }, [diag]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync classifier mode from diagnostics data (non-switching source of truth)
  useEffect(() => {
    if (diag?.classifier_mode && !modeSwitching) {
      setClassifierMode(diag.classifier_mode);
    }
  }, [diag, modeSwitching]);

  // Toggle classifier mode handler
  const handleModeSelect = useCallback(
    async (newMode) => {
      if (modeSwitching || !newMode || newMode === classifierMode) return;
      setModeSwitching(true);
      try {
        const url = nodeApiUrl("/classifier/mode");
        if (!url) return;
        const res = await fetchWithTimeout(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mode: newMode }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        setClassifierMode(data.mode || newMode);
      } catch (err) {
        console.error("[Weavian] Mode switch failed:", err);
        setError(err.message || "Failed to switch classifier mode");
      } finally {
        setModeSwitching(false);
      }
    },
    [classifierMode, modeSwitching, nodeApiUrl]
  );

  // Stats
  const deviceCount = diag?.devices?.length || 0;
  const activeCount = useMemo(() => {
    if (!diag?.devices) return 0;
    return diag.devices.filter((d) => (d.active_seconds ?? 0) <= 60).length;
  }, [diag]);

  const tunnelTX = fmtBytes(diag?.tunnel?.tx_bytes);
  const tunnelRX = fmtBytes(diag?.tunnel?.rx_bytes);

  const wanList = useMemo(() => {
    if (!diag?.wans) return [];
    return Object.values(diag.wans).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [diag]);

  const sys = diag?.system;
  const tc = diag?.tc_classifier;
  const carrying = wanList.filter((w) => w.active).length;

  // ── No node selected state ──
  if (!selectedNode) {
    return (
      <div className="dash-empty-state">
        <div className="dash-empty-icon">
          <FiMonitor size={48} />
        </div>
        <h2>Welcome to Weavian</h2>
        <p>Select an edge node from the sidebar to view your network.</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* ── Page header ── */}
      <div className="dash-header">
        <div>
          <h1 className="dash-page-title">Dashboard</h1>
          <p className="dash-page-sub">
            Network overview for <strong>{selectedNode}</strong>
            {lastFetch && (
              <span className="dash-refresh-ts">
                {" "}
                · Updated {lastFetch.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
      </div>

      {error && <div className="dash-error">{error}</div>}

      {/* ── Summary strip ── */}
      <div className="dash-stats-row">
        {phone ? (
          <SummaryCard
            icon={FiWifi}
            label="Links Carrying"
            value={diag ? `${carrying} of ${wanList.length}` : loading ? "Loading…" : "—"}
            sub={wanList.map((w) => w.name).join(" · ")}
            accent="#6366f1"
          />
        ) : (
          <SummaryCard
            icon={FiMonitor}
            label="Connected Devices"
            value={activeCount}
            sub={`${deviceCount} total`}
            accent="#6366f1"
          />
        )}
        <SummaryCard
          icon={FiActivity}
          label="Tunnel Traffic"
          value={`↑ ${tunnelTX}`}
          sub={`↓ ${tunnelRX}`}
          accent="#22d3ee"
        />
        {phone ? (
          <SummaryCard
            icon={FiClock}
            label="Connected For"
            value={diag?.uptime || (loading ? "Loading…" : "—")}
            sub="this session"
            accent="#22c55e"
          />
        ) : (
          <SummaryCard
            icon={FiCpu}
            label="System"
            value={sys?.uptime || (loading ? "Loading…" : "—")}
            sub={sys ? `${sys.num_cpu} CPUs · ${sys.heap_mb?.toFixed(1) || "—"} MB heap` : ""}
            accent="#22c55e"
          />
        )}
        {!phone && (
          <SummaryCard
            icon={FiLayers}
            label="Traffic Classifier"
            value={tc?.alive ? "Running" : loading ? "Loading…" : "Stopped"}
            sub={tc ? `${fmtNum(tc.flows)} flows · ${fmtNum(tc.classified)} classified` : ""}
            accent={tc?.alive ? "#22c55e" : "#ef4444"}
          />
        )}
      </div>

      {/* ── Classification Mode Toggle ── */}
      {classifierMode && (
        <div className="dash-mode-toggle-bar">
          <div className="dash-mode-info">
            <FiLayers size={16} />
            <span className="dash-mode-label">Classification Mode</span>
            <span className={`dash-mode-badge ${MODE_BADGE_CLASS[classifierMode] || "mode-cat"}`}>
              {MODE_LABEL[classifierMode] || classifierMode}
            </span>
          </div>
          <div className="dash-mode-seg" role="group" aria-label="Classification mode">
            {MODE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                className={`dash-mode-seg-btn ${classifierMode === opt.id ? "active" : ""}`}
                onClick={() => handleModeSelect(opt.id)}
                disabled={modeSwitching}
                title={MODE_HINT[opt.id]}
              >
                {opt.label}
              </button>
            ))}
            {modeSwitching && <span className="dash-mode-spinner" />}
          </div>
          <span className="dash-mode-hint">{MODE_HINT[classifierMode] || ""}</span>
        </div>
      )}

      {/* ── Network Canvas: the devices behind an edge, which a phone has none of ── */}
      {!phone && <div className="dash-canvas-section">
        <div className="dash-section-header">
          <h2 className="dash-section-title">Network Topology</h2>
          <div className="dash-canvas-controls">
            <span className="dash-device-count">
              {activeCount} active
              {deviceCount - activeCount > 0 &&
                ` · ${deviceCount - activeCount} stale`}
            </span>
            {deviceCount - activeCount > 0 && (
              <button
                className={`dash-hide-stale-btn ${hideStale ? "active" : ""}`}
                onClick={() => setHideStale((v) => !v)}
                title={hideStale ? "Show stale devices" : "Hide stale devices"}
              >
                {hideStale ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                {hideStale ? "Show stale" : "Hide stale"}
              </button>
            )}
          </div>
        </div>
        <NetworkCanvas
          devices={diag?.devices || []}
          selectedNode={selectedNode}
          onDeviceClick={(d) => setSelectedDevice(d)}
          wans={diag?.wans}
          systemHealth={sys}
          tunnelStats={diag?.tunnel}
          hideStale={hideStale}
        />
      </div>}

      {/* ── System Details (collapsible) ── */}
      <div className="dash-system-section">
        <button
          className="dash-system-toggle"
          onClick={() => setSystemExpanded((v) => !v)}
        >
          <span className="dash-system-toggle-label">
            <FiDatabase size={16} />
            {phone ? "Links and Tunnel" : "System Details"}
          </span>
          <span
            className={`dash-chevron ${systemExpanded ? "open" : ""}`}
          >
            ▾
          </span>
        </button>

        {systemExpanded && (
          <div className="dash-system-content">
            {/* System health */}
            {sys && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiCpu size={14} /> System Health
                </h3>
                <div className="dash-mini-grid">
                  <MiniCard label="Uptime" value={sys.uptime || "—"} />
                  <MiniCard label="Goroutines" value={fmtNum(sys.goroutines)} />
                  <MiniCard
                    label="Heap"
                    value={sys.heap_mb ? `${sys.heap_mb.toFixed(1)} MB` : "—"}
                  />
                  <MiniCard label="CPUs" value={fmtNum(sys.num_cpu)} />
                  <MiniCard
                    label="IP Forward"
                    value={sys.ip_forward ? "Enabled" : "Disabled"}
                    pill={sys.ip_forward ? "pill-ok" : "pill-err"}
                  />
                  <MiniCard label="Restarts" value={fmtNum(sys.restart_count)} />
                </div>
              </div>
            )}

            {/* Tunnel */}
            {diag?.tunnel && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiHardDrive size={14} /> Tunnel
                </h3>
                <div className="dash-mini-grid">
                  <MiniCard
                    label="TX"
                    value={fmtBytes(diag.tunnel.tx_bytes)}
                  />
                  <MiniCard
                    label="RX"
                    value={fmtBytes(diag.tunnel.rx_bytes)}
                  />
                  <MiniCard
                    label="TX Pkts"
                    value={fmtNum(diag.tunnel.tx_packets)}
                  />
                  <MiniCard
                    label="RX Pkts"
                    value={fmtNum(diag.tunnel.rx_packets)}
                  />
                  <MiniCard
                    label="Errors"
                    value={fmtNum(diag.tunnel.errors)}
                    pill={diag.tunnel.errors > 0 ? "pill-err" : "pill-ok"}
                  />
                </div>
              </div>
            )}

            {/* WAN links */}
            {wanList.length > 0 && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiWifi size={14} /> WAN Links
                </h3>
                <div className="dash-wan-list">
                  {wanList.map((w) => (
                    <WANCompact key={w.name} wan={w} />
                  ))}
                </div>
              </div>
            )}

            {/* DHCP leases count */}
            {diag?.dhcp_leases && diag.dhcp_leases.length > 0 && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiDatabase size={14} /> DHCP Leases
                </h3>
                <div className="dash-dhcp-list">
                  {diag.dhcp_leases.map((l, i) => (
                    <div key={`${l.ip}-${i}`} className="dash-dhcp-item">
                      <span className="dash-dhcp-ip">{l.ip}</span>
                      <span className="dash-dhcp-mac">{l.mac || "—"}</span>
                      <span className="dash-dhcp-host">
                        {l.hostname || "—"}
                      </span>
                      <span className="dash-dhcp-exp">
                        {l.expiry
                          ? new Date(l.expiry * 1000).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Device detail drawer ── */}
      {selectedDevice && (
        <DeviceDrawer
          device={selectedDevice}
          nodeId={selectedNode}
          onClose={() => setSelectedDevice(null)}
          classifierMode={classifierMode}
        />
      )}
    </div>
  );
}
